import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { embedTransaction } from "@/lib/embed-auth";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { ThemeProvider, useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Moon, Sun } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSpatialFocusNavigation } from "@/hooks/use-spatial-focus-navigation";
import NotFound from "@/pages/not-found";
import { ErrorBoundary } from "@/components/error-boundary";
import Dashboard from "@/pages/dashboard";
import ChatbotsPage from "@/pages/chatbots";
import CreateChatbot from "@/pages/create-chatbot";
import ChatbotDetail from "@/pages/chatbot-detail";
import ConversationsPage from "@/pages/conversations";
import ApiDocsPage from "@/pages/api-docs";
import SettingsPage from "@/pages/settings-page";
import AdminSettingsHub from "@/pages/admin-settings-hub";
import CompliancePage from "@/pages/compliance-page";
import LoginPage from "@/pages/login";
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";
import MarketingLanding from "@/pages/marketing-landing";
import AdminDashboard from "@/pages/admin-dashboard";
import ImpersonateHandoff from "@/pages/impersonate-handoff";
import OrgAdmin from "@/pages/org-admin";
import OrgLanding from "@/pages/org-landing";
import FullscreenChat from "@/pages/fullscreen-chat";
import { resolvePostAuthTarget } from "@/lib/post-auth-redirect";
import StaffPortal from "@/pages/staff-portal";
import PortalGateway from "@/pages/portal-gateway";
import PortalRoleSelect from "@/pages/portal-role-select";
import GroupsPage from "@/pages/groups";
import OrgRolesPage from "@/pages/org-roles";
import OrgUsersPage from "@/pages/org-users";
import RolesManagerPage from "@/pages/roles-manager";
import SsoSettingsPage from "@/pages/sso-settings";
import SsoMfaPage from "@/pages/sso-mfa";
import ChatbotIntegrationsPage from "@/pages/chatbot-integrations";
import IntegrationsPage from "@/pages/integrations";
import ChatbotToolsPage from "@/pages/chatbot-tools";
import ChatbotHistoryPage from "@/pages/chatbot-history";
import TenantHistoryPage from "@/pages/tenant-history";
import MyHistoryPage from "@/pages/my-history";
import AuditToolInvocationsPage from "@/pages/audit-tool-invocations";
import AuditGeneralPage from "@/pages/audit-general";
import AdminBrandingPage from "@/pages/admin-branding";
import AdminIntegrationsPage from "@/pages/admin-integrations";
import ApplicationLogsPage from "@/pages/application-logs";
import MasterLoginPage from "@/pages/master-login";
import BillingSelectPlanPage from "@/pages/billing-select-plan";
import BillingSuccessPage from "@/pages/billing-success";
import OnboardingRolesPage from "@/pages/onboarding-roles";
import OnboardingCompliancePage from "@/pages/onboarding-compliance";
import LiveChatPage from "@/pages/live-chat";
import AgentConsolePage from "@/pages/agent-console";
import LiveChatOfflinePage from "@/pages/live-chat-offline";
import LiveChatSettingsPage from "@/pages/live-chat-settings";
import SetupAgentPage from "@/pages/setup-agent";
import AiProvidersPage from "@/pages/ai-providers";
import ApprovalsPage from "@/pages/approvals";
import AgentCallWorkspace from "@/pages/calls/agent-workspace";
import SupervisorCallsDashboard from "@/pages/calls/supervisor-dashboard";
import AdminCallSettings from "@/pages/calls/admin-settings";
import CallLogs from "@/pages/calls/call-logs";
import VoicemailInbox from "@/pages/calls/voicemail";
import CallbackQueue from "@/pages/calls/callbacks";
import CallReports from "@/pages/calls/reports";
import TicketsList from "@/pages/tickets/list";
import TicketDetail from "@/pages/tickets/detail";
import UserSettingsPage from "@/pages/user-settings";
import ApiKeysPage from "@/pages/api-keys";
import SecurityEventsPage from "@/pages/security-events";
import NlpIntentsPage from "@/pages/nlp-intents";
import NlpEntitiesPage from "@/pages/nlp-entities";
import ContentGovernancePage from "@/pages/content-governance";
import FaqManagementPage from "@/pages/faq-management";
import DiscoveryWorkspacePage from "@/pages/discovery-workspace";
import TermsPage from "@/pages/terms-page";
import PrivacyPage from "@/pages/privacy-page";
import { FirstLoginTour } from "@/components/first-login-tour";
import { useBranding } from "@/hooks/use-branding";
import { isLiveChatRole, isLiveChatAdminRole, canUseAgentConsole, useFeature, hasPermission, useAuthBarrier } from "@/lib/auth";
import { SessionExpiryWarningDialog } from "@/components/session-expiry-warning";
import { useEffect, useState, useCallback } from "react";
import { TenantThemeVars } from "@/components/tenant-theme-vars";
import { HelpAssistantProvider } from "@/components/help-assistant-widget";
import { LiveChatNotificationBell } from "@/components/live-chat-notification-bell";

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="button-theme-toggle"
      aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}>
      {theme === "light" ? <Moon className="w-4 h-4" aria-hidden="true" /> : <Sun className="w-4 h-4" aria-hidden="true" />}
    </Button>
  );
}

// Roles that should see the admin/builder dashboard at "/". Everyone
// else (employee/manager/user/null) is treated as an end-user and is
// routed straight to the staff portal where they pick a bot to chat
// with — that's their only intended workflow.
const ADMIN_HOME_ROLES = new Set([
  "admin", "super_admin", "org_admin", "it_admin", "hr_admin", "bot_admin",
]);

function Router() {
  const { user, organization } = useAuth();
  const features: string[] = ((organization as any)?.features) || [];
  const has = (k: string) => user?.role === "super_admin" || features.includes(k);
  // Globally disabled module categories from /api/auth/me.
  // Super Admins always get an empty array (unrestricted).
  const disabledModules: string[] = ((organization as any)?.disabledModules) || [];
  const isModuleOn = (m: string) => user?.role === "super_admin" || !disabledModules.includes(m);
  // Non-admin staff land on the StaffPortal at "/", but rendered inside
  // AppLayout (sidebar + header) — admin nav items are visually present
  // but disabled by AppSidebar so staff see what the platform offers
  // without being able to act on it.
  const isAdminHome = ADMIN_HOME_ROLES.has(user?.role || "");
  const isLiveOnly = isLiveChatRole(user?.role);
  // Staff users get ONLY the portal routes mounted client-side. The
  // sidebar already disables admin nav, but we also gate the routes
  // themselves so a staff user typing /chatbots, /conversations, or
  // /settings into the URL bar lands on NotFound instead of loading
  // an admin page that would only render with broken/empty data
  // (the server already 401s the underlying APIs).
  // Live-chat-only roles get the same treatment: they see ONLY the
  // /live-chat routes (and the / landing page that redirects them).
  const isStaff = !isAdminHome && !isLiveOnly;
  // Live-chat-only users land on /live-chat instead of the staff portal.
  // Admins land on the analytics Dashboard only when the plan grants the
  // "dashboard" capability; otherwise they fall back to the staff portal
  // so "/" never renders a plan-revoked screen.
  const homeComponent = isAdminHome ? (has("dashboard") ? Dashboard : StaffPortal) : (isLiveOnly ? LiveChatPage : StaffPortal);
  return (
    <>
    <Switch>
      <Route path="/" component={homeComponent} />
      {/* Legacy alias: older login responses / bookmarks point at
          "/dashboard"; the real dashboard route is "/". Redirecting keeps
          direct access and refresh from landing on the 404 page. */}
      <Route path="/dashboard"><Redirect to="/" /></Route>
      {/* Impersonation handoff — public route; token arrives via URL hash, stored in sessionStorage */}
      <Route path="/impersonate" component={ImpersonateHandoff} />
      {/* Live Chat — visible to live_* roles AND to org/super admins */}
      {/* Hidden live-agent & supervisor console — reachable only by URL,
          never surfaced in the sidebar. Agents/supervisors/admins only. */}
      {canUseAgentConsole(user) && <Route path="/agent-console" component={AgentConsolePage} />}
      {has("livechat.operator") && (hasPermission(user, "livechat.operate") || hasPermission(user, "livechat.supervise") || hasPermission(user, "livechat.admin")) && <Route path="/live-chat" component={LiveChatPage} />}
      {has("livechat.offline") && hasPermission(user, "livechat.admin") && <Route path="/live-chat/offline" component={LiveChatOfflinePage} />}
      {has("livechat.settings") && hasPermission(user, "livechat.admin") && <Route path="/live-chat/settings" component={LiveChatSettingsPage} />}
      {isModuleOn("bots") && has("chatbots") && !isStaff && !isLiveOnly && hasPermission(user, "chatbots.view") && <Route path="/chatbots" component={ChatbotsPage} />}
      {isModuleOn("bots") && has("chatbots") && !isStaff && !isLiveOnly && hasPermission(user, "chatbots.manage") && <Route path="/chatbots/new" component={CreateChatbot} />}
      {isModuleOn("bots") && has("chatbots") && !isStaff && !isLiveOnly && hasPermission(user, "chatbots.view") && <Route path="/chatbots/:id" component={ChatbotDetail} />}
      {isModuleOn("bots") && has("chatbots") && !isStaff && !isLiveOnly && hasPermission(user, "tools.view") && <Route path="/chatbots/:id/tools" component={ChatbotToolsPage} />}
      {isModuleOn("bots") && has("integrations") && !isStaff && !isLiveOnly && hasPermission(user, "integrations.manage") && <Route path="/chatbots/:id/integrations" component={ChatbotIntegrationsPage} />}
      {has("integrations") && !isStaff && !isLiveOnly && hasPermission(user, "integrations.manage") && <Route path="/integrations" component={IntegrationsPage} />}
      {isModuleOn("bots") && has("chatbots") && !isStaff && !isLiveOnly && hasPermission(user, "conversations.view") && <Route path="/chatbots/:id/history" component={ChatbotHistoryPage} />}
      {has("organization.history") && !isStaff && !isLiveOnly && hasPermission(user, "conversations.view") && <Route path="/history" component={TenantHistoryPage} />}
      {/* Personal history: every authenticated user who holds conversations.view
          (seeded for Members by default). No plan-feature gate, no !isStaff
          restriction — this route is specifically for staff users viewing their
          own threads. Admins also land here if they navigate directly. */}
      {!isLiveOnly && hasPermission(user, "conversations.view") && <Route path="/my-history" component={MyHistoryPage} />}
      {/* User settings — available to every authenticated non-staff user (RTM Req 48-52). */}
      <Route path="/profile" component={UserSettingsPage} />
      {/* Entitlement-gated: plan feature `conversations` + the
          `conversations.view` permission (super_admin bypasses both). */}
      {(user?.role === "super_admin" || (has("conversations") && !isStaff && !isLiveOnly && hasPermission(user, "conversations.view"))) && <Route path="/conversations" component={ConversationsPage} />}
      {has("apidocs") && !isStaff && !isLiveOnly && hasPermission(user, "api_docs.manage") && <Route path="/api-docs" component={ApiDocsPage} />}
      {has("settings") && !isStaff && !isLiveOnly && hasPermission(user, "settings.manage") && <Route path="/settings" component={SettingsPage} />}
      {/* RTM Req 52 — consolidated admin settings hub with role-conditional tiles */}
      {!isStaff && !isLiveOnly && <Route path="/admin-settings" component={AdminSettingsHub} />}
      {has("compliance") && !isStaff && !isLiveOnly && hasPermission(user, "compliance.view") && <Route path="/compliance" component={CompliancePage} />}
      {isModuleOn("bots") && has("setup_agent") && !isStaff && !isLiveOnly && hasPermission(user, "chatbots.manage") && <Route path="/setup-agent" component={SetupAgentPage} />}
      {/* /ai-providers redirects to the unified Integrations page (AI Model tab) */}
      <Route path="/ai-providers"><Redirect to="/integrations" /></Route>
      {/* NLP intent/entity management — same permission gate as bot configuration */}
      {isModuleOn("bots") && !isStaff && !isLiveOnly && hasPermission(user, "chatbots.manage") && <Route path="/nlp/intents" component={NlpIntentsPage} />}
      {isModuleOn("bots") && !isStaff && !isLiveOnly && hasPermission(user, "chatbots.manage") && <Route path="/nlp/entities" component={NlpEntitiesPage} />}
      {isModuleOn("bots") && has("chatbots") && !isStaff && !isLiveOnly && (hasPermission(user, "knowledge.manage") || hasPermission(user, "content.review") || hasPermission(user, "content.publish")) && <Route path="/content-governance" component={ContentGovernancePage} />}
      {/* FAQ Management route — permission-only gate so custom tenant roles
          (e.g. Content_Manager) with faq.view can reach this page.
          Member/staff users never hold faq.view, so they remain blocked. */}
      {hasPermission(user, "faq.view") && <Route path="/faq-management" component={FaqManagementPage} />}
      {(user?.role === "super_admin" || user?.role === "org_admin") && <Route path="/discovery-workspace" component={DiscoveryWorkspacePage} />}
      {user?.role === "super_admin" && <Route path="/admin" component={AdminDashboard} />}
      {user?.role === "super_admin" && <Route path="/admin/organizations" component={AdminDashboard} />}
      {user?.role === "super_admin" && <Route path="/admin/users" component={AdminDashboard} />}
      {user?.role === "super_admin" && <Route path="/admin/branding" component={AdminBrandingPage} />}
      {user?.role === "super_admin" && <Route path="/admin/integrations" component={AdminIntegrationsPage} />}
      {user?.role === "super_admin" && <Route path="/logs" component={ApplicationLogsPage} />}
      {has("organization.manage") && !isStaff && !isLiveOnly && hasPermission(user, "settings.manage") && <Route path="/admin/org" component={OrgAdmin} />}
      {has("workforce") && hasPermission(user, "groups.manage") && <Route path="/settings/groups" component={GroupsPage} />}
      {has("users.manage") && hasPermission(user, "users.manage") && <Route path="/admin/org/users" component={OrgUsersPage} />}
      {has("roles.manage") && hasPermission(user, "roles.manage") && <Route path="/admin/org/roles" component={RolesManagerPage} />}
      {has("users.manage") && hasPermission(user, "users.manage") && <Route path="/admin/org/team" component={OrgRolesPage} />}
      {has("sso") && hasPermission(user, "sso.manage") && <Route path="/settings/sso" component={SsoSettingsPage} />}
      {has("audit") && hasPermission(user, "audit.view") && <Route path="/admin/audit" component={AuditGeneralPage} />}
      {has("audit") && hasPermission(user, "tools.view") && <Route path="/admin/audit/tools" component={AuditToolInvocationsPage} />}
      {has("audit") && hasPermission(user, "audit.view") && <Route path="/admin/security" component={SecurityEventsPage} />}
      {/* Approvals queue — visible to anyone with tools.view (org admins + managers). */}
      {hasPermission(user, "tools.view") && !isStaff && !isLiveOnly && <Route path="/approvals" component={ApprovalsPage} />}
      {/* Calls module */}
      {has("calls.workspace") && hasPermission(user, "calls.operate") && <Route path="/calls" component={AgentCallWorkspace} />}
      {has("calls.logs") && hasPermission(user, "calls.view") && <Route path="/calls/logs" component={CallLogs} />}
      {has("calls.voicemail") && hasPermission(user, "calls.view") && <Route path="/calls/voicemail" component={VoicemailInbox} />}
      {has("calls.callbacks") && hasPermission(user, "calls.view") && <Route path="/calls/callbacks" component={CallbackQueue} />}
      {has("calls.supervisor") && hasPermission(user, "calls.supervise") && <Route path="/calls/supervisor" component={SupervisorCallsDashboard} />}
      {has("calls.reports") && hasPermission(user, "calls.reports.view") && <Route path="/calls/reports" component={CallReports} />}
      {has("calls.settings") && hasPermission(user, "calls.admin") && <Route path="/calls/settings" component={AdminCallSettings} />}
      {/* Tickets module */}
      {has("tickets") && hasPermission(user, "tickets.view") && <Route path="/tickets" component={TicketsList} />}
      {has("tickets") && hasPermission(user, "tickets.view") && <Route path="/tickets/:id" component={TicketDetail} />}
      {!isStaff && !isLiveOnly && hasPermission(user, "api_keys.manage") && <Route path="/api-keys" component={ApiKeysPage} />}
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function AppLayout() {
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  const branding = useBranding();
  const { user, organization } = useAuth();
  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full app-bg">
        {/* Skip link (WCAG 2.4.1): first tabbable control in the admin shell,
            visible on keyboard focus, moves focus into the main region. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:bg-background focus:text-foreground focus:px-3 focus:py-2 focus:rounded-md focus:shadow-lg"
          onClick={(e) => {
            e.preventDefault();
            const main = document.getElementById("main-content");
            if (main) {
              main.focus();
              main.scrollIntoView({ block: "start" });
            }
          }}
          data-testid="link-skip-to-main"
        >
          Skip to main content
        </a>
        {/* WCAG 2.2.1 — warn authenticated users before their JWT expires */}
        <SessionExpiryWarningDialog />
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="app-header sticky top-0 z-30 flex items-center justify-between gap-3 px-4 h-14">
            <div className="flex items-center gap-3 min-w-0">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <div className="hidden md:flex items-center gap-2 min-w-0">
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
                  {branding.productName}
                </span>
                {organization?.name && (
                  <>
                    <span className="text-muted-foreground/40">/</span>
                    <span className="text-sm font-medium truncate" data-testid="text-header-org">
                      {organization.name}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium brand-pill"
                data-testid="pill-status-live"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                Live
              </span>
              {(user?.email || user?.role) && (
                <div className="hidden md:flex flex-col items-end leading-tight">
                  {user?.email && (
                    <span className="text-[11px] text-muted-foreground truncate max-w-[160px]">
                      {user.email}
                    </span>
                  )}
                  {user?.role && (
                    <span className="text-[11px] font-bold text-muted-foreground truncate max-w-[160px]" data-testid="text-header-role">
                      {user.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                  )}
                </div>
              )}
              <LiveChatNotificationBell />
              <ThemeToggle />
            </div>
          </header>
          <main id="main-content" tabIndex={-1} data-focus-region="main" className="flex-1 overflow-auto focus:outline-none">
            <ErrorBoundary>
              <HelpAssistantProvider>
                <Router />
              </HelpAssistantProvider>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

// Known authenticated app sections. An anonymous visitor deep-linking into
// one of these should see the sign-in page (and can return after login); any
// other URL is a genuine 404 and renders the accessible Not Found page.
const KNOWN_APP_PREFIXES =
  /^\/(dashboard|chatbots|conversations|history|my-history|api-docs|settings|compliance|setup-agent|ai-providers|integrations|admin|logs|live-chat|agent-console|calls|tickets|billing)(\/|$)/;

function UnauthenticatedFallback() {
  const [location] = useLocation();
  // Deep link into a real (auth-gated) section: send the visitor to the
  // sign-in page carrying the original destination, so a successful login
  // (or session restore) returns them exactly where they were headed.
  return KNOWN_APP_PREFIXES.test(location)
    ? <Redirect to={`/login?redirect=${encodeURIComponent(location)}`} replace />
    : <NotFound />;
}

/**
 * Role-based home selection. Super Admins land on the Platform Admin Console
 * (/admin); all other authenticated roles land on "/" (their homeComponent).
 * Centralised here so both AuthedLoginRedirect and goAfterAuth use the same
 * logic and can't diverge.
 */
function roleHome(role?: string | null): string {
  if (role === "super_admin") return "/admin";
  return "/";
}

/**
 * Authenticated user visiting /login or /signup (e.g. the moment auth state
 * flips after a successful sign-in, or a stale bookmark): declaratively
 * redirect to the requested destination or the role-based home.
 * Single redirect source — prevents a sign-in flash and duplicate redirects.
 */
function AuthedLoginRedirect() {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const { user, organization } = useAuth();
  // Resume incomplete onboarding even when the user navigates directly to /login.
  if ((organization as any)?.needsRolesOnboarding) {
    return <Redirect to="/onboarding/roles" replace />;
  }
  return <Redirect to={resolvePostAuthTarget(params.get("redirect"), roleHome(user?.role))} replace />;
}

// Neutral loading screen — shown while auth state is unknown and during
// bfcache revalidation. Contains no user data, no org data, no private DOM.
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center" aria-live="polite" aria-label="Verifying session">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground text-sm">Loading…</p>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const { isLoading, isAuthenticated, user, organization } = useAuth();
  const [location] = useLocation();

  // bfcacheBlocking is true while we are re-validating /api/auth/me after a
  // bfcache page restore. Protected content must not render during that window.
  const [bfcacheBlocking, setBfcacheBlocking] = useState(false);
  const onBfcacheBlock = useCallback(() => setBfcacheBlocking(true), []);
  const onBfcacheUnblock = useCallback(() => setBfcacheBlocking(false), []);
  useAuthBarrier(onBfcacheBlock, onBfcacheUnblock);

  // Transaction completion must remain reachable after useLogin primes auth,
  // and before ordinary login/onboarding redirects take over.
  if (location === "/login" && embedTransaction(window.location.search)) {
    return <LoginPage />;
  }
  // The challenge authenticates itself server-side. An unrelated existing SPA
  // session must not divert an SSO challenge into billing/onboarding.
  if (location === "/sso/mfa") return <SsoMfaPage />;

  // Never render protected content while auth state is unknown or while a
  // bfcache restore is being validated. Show only a neutral loading screen.
  if (isLoading || bfcacheBlocking) {
    return (
      <Switch>
        <Route path="/chat/:apiKey" component={FullscreenChat} />
        <Route path="/widget/:apiKey" component={FullscreenChat} />
        <Route><AuthLoadingScreen /></Route>
      </Switch>
    );
  }

  if (!isAuthenticated) {
    return (
      <Switch>
        <Route path="/chat/:apiKey" component={FullscreenChat} />
        <Route path="/widget/:apiKey" component={FullscreenChat} />
        <Route path="/org/:slug" component={OrgLanding} />
        {/* The portal is an independent gateway: guests get straight in,
            staff are sent to sign in. The staff bot picker itself always
            requires auth. */}
        <Route path="/portal/:apiKey/staff" component={PortalRoleSelect} />
        <Route path="/portal/staff"><Redirect to="/login" /></Route>
        <Route path="/portal/:apiKey" component={PortalGateway} />
        <Route path="/portal" component={PortalGateway} />
        <Route path="/master/login" component={MasterLoginPage} />
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/reset-password" component={ResetPasswordPage} />
        <Route path="/terms" component={TermsPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/signup" component={LoginPage} />
        <Route path="/login" component={LoginPage} />
        {/* SSO MFA verification — public; challenge nonce arrives as ?challenge=…
            after the IdP redirect. Must appear in the unauthenticated switch so
            users land here before they hold a session token. */}
        <Route path="/sso/mfa" component={SsoMfaPage} />
        <Route path="/" component={MarketingLanding} />
        {/* WCAG 2.4.2/2.4.4: only real (but auth-gated) app routes fall back
            to the sign-in page; genuinely unknown URLs get an accessible
            Not Found page instead of silently rendering Login. */}
        <Route><UnauthenticatedFallback /></Route>
      </Switch>
    );
  }

  // Onboarding lock: new orgs that haven't completed built-in role selection
  // are sent to the roles page. This takes priority over the billing lock so
  // the onboarding flow always runs to completion before billing is attempted.
  const needsRolesOnboarding = !!(organization as any)?.needsRolesOnboarding;
  if (needsRolesOnboarding) {
    return (
      <Switch>
        <Route path="/onboarding/roles" component={OnboardingRolesPage} />
        {/* Everything else funnels back to role selection. */}
        <Route><Redirect to="/onboarding/roles" replace /></Route>
      </Switch>
    );
  }

  // Hard-lock: any signed-in user whose org has not paid is funneled
  // into the billing flow. Only billing routes render; everything else
  // (including bookmarks deep-linked into the app) redirects to plan
  // selection. The Stripe webhook flips subscriptionStatus → 'active',
  // /api/auth/me re-polls, and the guard releases on the next tick.
  const requiresBilling = !!(organization as any)?.requiresBilling;
  if (requiresBilling) {
    return (
      <Switch>
        <Route path="/billing/success" component={BillingSuccessPage} />
        <Route path="/billing/select-plan" component={BillingSelectPlanPage} />
        {/* Built-in role selection runs BEFORE billing during signup.
            Must be listed here so it is reachable while requiresBilling=true
            (new orgs are in pending/unpaid status until Stripe confirms). */}
        <Route path="/onboarding/roles" component={OnboardingRolesPage} />
        {/* Compliance framework setup runs AFTER role selection, BEFORE billing. */}
        <Route path="/onboarding/compliance" component={OnboardingCompliancePage} />
        {/* Redirect (not inline-render) so the URL always reflects the
            billing lock — otherwise a post-login /login?redirect=… URL
            sticks around while the plan page shows. */}
        <Route><Redirect to="/billing/select-plan" replace /></Route>
      </Switch>
    );
  }

  // Staff portal lives inside AppLayout (handled by Router below) so
  // staff see the dashboard chrome with admin features disabled. Only
  // the standalone /chat/:apiKey and /org/:slug routes stay full-screen
  // (the latter so tenant admins can preview their own public landing
  // page without it being wrapped in the staff dashboard chrome).
  const isAdminHome = ADMIN_HOME_ROLES.has(user?.role || "");
  // Suppress the tour on any billing or onboarding route. In the normal
  // new-user flow these routes are guarded by the requiresBilling /
  // needsRolesOnboarding early returns above, but a user can also navigate
  // to /billing/select-plan directly while on an active plan (e.g. to
  // upgrade). Showing the tour there is premature and a dismissal would
  // permanently suppress it before the user ever reaches the dashboard.
  const tourSuppressedOnThisRoute =
    location.startsWith("/billing") || location.startsWith("/onboarding");
  return (
    <>
    <Switch>
      {/* Signed-in users never see the sign-in page — resolves the post-login
          route without any flash and honors ?redirect deep links. */}
      <Route path="/login" component={AuthedLoginRedirect} />
      <Route path="/signup" component={AuthedLoginRedirect} />
      <Route path="/chat/:apiKey" component={FullscreenChat} />
      <Route path="/widget/:apiKey" component={FullscreenChat} />
      <Route path="/org/:slug" component={OrgLanding} />
      {/* Independent portal — rendered full-screen (no admin chrome) so
          it behaves the same whether reached from the sidebar or directly. */}
      <Route path="/portal/:apiKey/staff" component={PortalRoleSelect} />
      <Route path="/portal/staff" component={StaffPortal} />
      <Route path="/portal/:apiKey" component={PortalGateway} />
      <Route path="/portal" component={PortalGateway} />
      <Route path="/master/login" component={MasterLoginPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/billing/success" component={BillingSuccessPage} />
      <Route path="/billing/select-plan" component={BillingSelectPlanPage} />
      {/* Role-selection step shown immediately after signup, before billing. */}
      <Route path="/onboarding/roles" component={OnboardingRolesPage} />
      {/* Compliance framework setup — after roles, before billing. */}
      <Route path="/onboarding/compliance" component={OnboardingCompliancePage} />
      {/* SSO MFA verification page — public; no auth required, challenge nonce
          arrives as ?challenge=… after the IdP redirect. Must be here (outside
          AppLayout) so unauthenticated users can reach it. */}
      <Route path="/sso/mfa" component={SsoMfaPage} />
      <Route><AppLayout /></Route>
    </Switch>
    {/* First-login guided tour: rendered at app level so it shows for all
        admin users regardless of which home component they land on (Dashboard
        when the plan includes "dashboard", StaffPortal otherwise). Tracks
        dismissal in localStorage — shows exactly once per browser profile.
        Excluded from billing/onboarding routes so the user is never prompted
        to dismiss it before they finish setup — that was the root cause of the
        tour being marked seen before the user ever reached the dashboard. */}
    {isAdminHome && !tourSuppressedOnThisRoute && <FirstLoginTour role={user?.role} userId={(user as any)?.id} />}
    </>
  );
}

// Per-route page titles (WCAG 2.4.2). Ordered prefix matchers — first hit
// wins; unknown paths fall back to the branded default. Titles update on
// every client-side navigation via wouter's useLocation.
const ROUTE_TITLES: Array<[RegExp, string]> = [
  [/^\/login/, "Sign in"],
  [/^\/signup/, "Sign up"],
  [/^\/forgot-password/, "Forgot password"],
  [/^\/reset-password/, "Reset password"],
  [/^\/master\/login/, "Master admin sign-in"],
  [/^\/chat\//, "Chat"],
  [/^\/widget\//, "Chat"],
  [/^\/portal/, "Portal"],
  [/^\/org\//, "Organization"],
  [/^\/billing\/success/, "Billing confirmed"],
  [/^\/billing/, "Choose a plan"],
  [/^\/dashboard$/, "Dashboard"],
  [/^\/chatbots\/new/, "New chatbot"],
  [/^\/chatbots\/[^/]+\/tools/, "Chatbot tools"],
  [/^\/chatbots\/[^/]+\/integrations/, "Chatbot integrations"],
  [/^\/chatbots\/[^/]+\/history/, "Chatbot history"],
  [/^\/chatbots\/[^/]+/, "Chatbot details"],
  [/^\/chatbots/, "Chatbots"],
  [/^\/conversations/, "Conversations"],
  [/^\/my-history/, "My history"],
  [/^\/history/, "Conversation history"],
  [/^\/api-docs/, "API documentation"],
  [/^\/settings\/groups/, "Workforce groups"],
  [/^\/settings\/sso/, "SSO settings"],
  [/^\/settings/, "Settings"],
  [/^\/compliance/, "Compliance Center"],
  [/^\/setup-agent/, "Setup agent"],
  [/^\/ai-providers/, "AI providers"],
  [/^\/integrations/, "Integrations"],
  [/^\/admin\/org\/users/, "Users"],
  [/^\/admin\/org\/roles/, "Roles"],
  [/^\/admin\/org\/team/, "Team"],
  [/^\/admin\/org/, "Organization admin"],
  [/^\/admin\/branding/, "Branding"],
  [/^\/admin\/integrations/, "Platform integrations"],
  [/^\/admin\/audit\/tools/, "Tool audit log"],
  [/^\/admin\/audit/, "General audit log"],
  [/^\/admin/, "Platform admin"],
  [/^\/live-chat\/settings/, "Live chat settings"],
  [/^\/live-chat\/offline/, "Offline messages"],
  [/^\/live-chat/, "Live chat"],
  [/^\/agent-console/, "Agent console"],
  [/^\/calls\/logs/, "Call logs"],
  [/^\/calls\/voicemail/, "Voicemail"],
  [/^\/calls\/callbacks/, "Callbacks"],
  [/^\/calls\/supervisor/, "Call supervision"],
  [/^\/calls\/reports/, "Call reports"],
  [/^\/calls\/settings/, "Call settings"],
  [/^\/calls/, "Calls"],
  [/^\/tickets\/[^/]+/, "Ticket details"],
  [/^\/tickets/, "Tickets"],
];

function DocumentTitle() {
  const branding = useBranding();
  const [location] = useLocation();
  useEffect(() => {
    const match = ROUTE_TITLES.find(([re]) => re.test(location));
    document.title = match
      ? `${match[1]} — ${branding.productName}`
      : `${branding.productName} — ${branding.tagline}`;
  }, [location, branding.productName, branding.tagline]);
  return null;
}

function App() {
  // Application-wide spatial Arrow-key focus navigation (additional to Tab).
  useSpatialFocusNavigation();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <DocumentTitle />
          <TenantThemeVars />
          <AuthenticatedApp />
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
